// frontend/src/components/Layout/Navbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faBox,
  faTruck,
  faShoppingCart,
  faSignOutAlt,
  faSignInAlt,
  faTachometerAlt,
  faList,
  faBars,
  faTimes,
  faUserPlus,
  faChevronDown,
  faCog,
  faInfoCircle,
  faEnvelope,
  faShieldAlt,
  faClipboardList,
  faMoneyBillWave,
  faUserCircle,
  faStore,
  faTractor,
  faCrown,
  faCalendarCheck,
  faSeedling,
  faPhone,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useCurrency } from "../../context/CurrencyContext";
import CurrencySelector from "../UI/CurrencySelector";
import logoImage from "../../assets/Logo.png";
import "./navbar.scss";

// Navigation Links
const NAV_LINKS = [
  { to: "/", label: "Home", icon: faHome },
  { to: "/about", label: "About", icon: faInfoCircle },
  { to: "/products", label: "Products", icon: faBox },
  { to: "/preorder", label: "Pre-Order", icon: faCalendarCheck },
  { to: "/track-order", label: "Track Order", icon: faTruck },
  { to: "/contact", label: "Contact", icon: faEnvelope },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { user, logout, isAdmin, isSuperAdmin, isFarmer, isSupplier } =
    useAuth();
  const { getCartCount, cart } = useCart();
  const { getSymbol } = useCurrency();
  const navigate = useNavigate();

  // Effects
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handlers
  const handleLogout = () => {
    logout();
    navigate("/");
    setIsOpen(false);
    setDropdownOpen(false);
  };

  const handleNavClick = () => {
    setIsOpen(false);
    setDropdownOpen(false);
  };

  // Cart count
  const cartCount = getCartCount ? getCartCount() : 0;
  const cartItemCount = cart?.items?.length || 0;
  const totalCount = Math.max(cartCount, cartItemCount);

  // User helpers with improved image support
  const getUserAvatar = () => {
    if (avatarError) return null;
    if (!user) return null;
    
    // Check multiple possible avatar sources
    const avatarSource = user?.profileImage || user?.avatar || user?.image || user?.profilePicture || null;
    
    // If it's a base64 string or URL, return it directly
    if (avatarSource && (avatarSource.startsWith('data:') || avatarSource.startsWith('http'))) {
      return avatarSource;
    }
    
    return null;
  };

  const getUserInitials = () => {
    if (!user?.name) return "U";
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserRoleLabel = () => {
    if (isSuperAdmin?.()) return "Super Admin";
    if (isAdmin?.()) return "Administrator";
    if (isFarmer?.()) return "Farmer";
    if (isSupplier?.()) return "Supplier";
    return "Customer";
  };

  const getUserRoleIcon = () => {
    if (isSuperAdmin?.() || isAdmin?.()) return faCrown;
    if (isFarmer?.()) return faTractor;
    if (isSupplier?.()) return faStore;
    return faUserCircle;
  };

  const userAvatar = getUserAvatar();

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="nav-container">
        {/* Logo */}
        <Link to="/" className="logo" onClick={handleNavClick}>
          {logoImage && (
            <img src={logoImage} alt="Kenali Enterprise" className="logo-img" />
          )}
          <span className="logo-text">
            Kenali <span className="logo-highlight">Enterprise</span>
          </span>
          {!scrolled && (
            <span className="currency-indicator">
              <FontAwesomeIcon icon={faMoneyBillWave} />
              {getSymbol()}
            </span>
          )}
        </Link>

        {/* Mobile Toggle */}
        <button
          className="mobile-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
        </button>

        {/* Navigation Links */}
        <div
          className={`nav-links ${isOpen ? "active" : ""}`}
          ref={mobileMenuRef}
        >
          {/* Main Nav Links */}
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={handleNavClick}
              className="nav-link"
            >
              <FontAwesomeIcon icon={link.icon} />
              <span>{link.label}</span>
            </Link>
          ))}

          {/* Currency Selector */}
          <div className="currency-wrapper">
            <CurrencySelector />
          </div>

          {/* Cart Link */}
          <Link to="/cart" onClick={handleNavClick} className="cart-link">
            <FontAwesomeIcon icon={faShoppingCart} />
            <span className="cart-label">Cart</span>
            {totalCount > 0 && <span className="cart-badge">{totalCount}</span>}
          </Link>

          {/* User Dropdown / Auth Links */}
          {user ? (
            <div className="user-dropdown" ref={dropdownRef}>
              <button
                className="dropdown-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-label="User menu"
              >
                <div className="user-avatar">
                  {userAvatar ? (
                    <img
                      src={userAvatar}
                      alt={user.name}
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <span>{getUserInitials()}</span>
                  )}
                </div>
                <span className="user-name">
                  {user.name?.split(" ")[0] || "Account"}
                </span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}
                />
              </button>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    className="dropdown-menu"
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="dropdown-header">
                      <div className="dropdown-user-info">
                        <div className="dropdown-user-avatar">
                          {userAvatar ? (
                            <img
                              src={userAvatar}
                              alt={user.name}
                              onError={() => setAvatarError(true)}
                            />
                          ) : (
                            <span>{getUserInitials()}</span>
                          )}
                        </div>
                        <div>
                          <div className="dropdown-user-name">{user.name}</div>
                          <div className="dropdown-user-email">
                            {user.email}
                          </div>
                          <div className="dropdown-user-role">
                            <FontAwesomeIcon icon={getUserRoleIcon()} />
                            <span>{getUserRoleLabel()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="dropdown-divider" />

                    <Link
                      to="/dashboard"
                      className="dropdown-item"
                      onClick={handleNavClick}
                    >
                      <FontAwesomeIcon icon={faTachometerAlt} />
                      <span>Dashboard</span>
                    </Link>

                    <Link
                      to="/orders"
                      className="dropdown-item"
                      onClick={handleNavClick}
                    >
                      <FontAwesomeIcon icon={faClipboardList} />
                      <span>My Orders</span>
                    </Link>

                    <Link
                      to="/profile"
                      className="dropdown-item"
                      onClick={handleNavClick}
                    >
                      <FontAwesomeIcon icon={faCog} />
                      <span>Account Settings</span>
                    </Link>

                    {(isAdmin?.() || isSuperAdmin?.()) && (
                      <>
                        <div className="dropdown-divider" />
                        <Link
                          to="/admin"
                          className="dropdown-item admin"
                          onClick={handleNavClick}
                        >
                          <FontAwesomeIcon icon={faShieldAlt} />
                          <span>Admin Panel</span>
                        </Link>
                      </>
                    )}

                    <div className="dropdown-divider" />

                    <button
                      className="dropdown-item logout"
                      onClick={handleLogout}
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      <span>Logout</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <>
              <Link to="/login" onClick={handleNavClick} className="login-link">
                <FontAwesomeIcon icon={faSignInAlt} />
                <span>Login</span>
              </Link>
              <Link
                to="/register"
                onClick={handleNavClick}
                className="register-link"
              >
                <FontAwesomeIcon icon={faUserPlus} />
                <span>Register</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;